import React from 'react';

import { ActionIcon, Alert, Box, BoxProps, CopyButton, Flex, Space, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { DNSRecord } from 'shared/interfaces/WhiteLabelDomain';

const COPY_TIMEOUT = 2000;

interface DNSRecordTableProps extends BoxProps {
  record: DNSRecord;
  alert?: React.ReactNode;
}

export function DNSRecordTable({ record, alert, ...boxProps }: DNSRecordTableProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const thStyle: React.CSSProperties = { textAlign: 'left', padding: 12, fontWeight: 500, fontSize: '0.80rem' };

  const tdStyle: React.CSSProperties = { padding: 12 };

  const ths = (
    <tr style={{ backgroundColor: theme.colors.gray[0] }}>
      <th style={{ ...thStyle }}>{t('WHITE_LABEL.RECORDS.TYPE')}</th>
      <th style={{ ...thStyle }}>{t('WHITE_LABEL.RECORDS.HOST')}</th>
      <th style={{ ...thStyle }}>{t('WHITE_LABEL.RECORDS.VALUE')}</th>
    </tr>
  );

  const rows = (
    <tr>
      <td style={{ ...tdStyle }}>{record.type}</td>
      <td style={{ ...tdStyle }}>
        <Flex direction="row" align="center">
          {record.host.split('.')[0]}
          <Space w={5} />
          <CopyButton value={record.host.split('.')[0]} timeout={COPY_TIMEOUT}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? t('APP.ACTIONS.COPIED') : t('APP.ACTIONS.COPY')} withArrow position="right">
                <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                  {copied ? (
                    <IconCheck style={{ width: 14, height: 14 }} />
                  ) : (
                    <IconCopy style={{ width: 14, height: 14 }} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Flex>
      </td>
      <td style={{ ...tdStyle }}>
        <Flex direction="row" align="center">
          {record.value}
          <Space w={5} />
          <CopyButton value={record.value} timeout={COPY_TIMEOUT}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? t('APP.ACTIONS.COPIED') : t('APP.ACTIONS.COPY')} withArrow position="right">
                <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                  {copied ? (
                    <IconCheck style={{ width: 14, height: 14 }} />
                  ) : (
                    <IconCopy style={{ width: 14, height: 14 }} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Flex>
      </td>
    </tr>
  );

  return (
    <Box style={{ border: `1px solid ${theme.other.gray.border}`, borderRadius: 10 }} {...boxProps}>
      {React.isValidElement(alert)
        ? React.cloneElement(alert as React.ReactElement<React.ComponentProps<typeof Alert>>, {
            styles: {
              root: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              ...(alert.props.styles || {})
            }
          })
        : alert}
      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>{ths}</thead>
        <tbody>{rows}</tbody>
      </table>
    </Box>
  );
}
